import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { useInvestorBankAccounts } from "./client/account";
import {
  getMandatesApi,
  createMandateApi,
  deleteMandateApi,
  authorizeMandateApi,
  authMandateApi,
  pollMandateStatusApi,
  generateMandateStatusReportApi,
  fetchMandateByIdApi,
  uploadMandateNachFormApi,
  generatePrefilledNACHMandate,
  getBankServiceCapabilitiesByIfscApi,
} from "../api/mandate";

export const bankServiceCapabilitiesByIfscQueryOptions = (
  ifscCode: string,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  queryOptions({
    queryKey: ["e-mandate-bank-list/by-ifsc", ifscCode],
    queryFn: () => getBankServiceCapabilitiesByIfscApi(ifscCode),
    enabled: enabled && !!ifscCode,
    staleTime: Infinity,
  });

export function useBankServiceCapabilitiesByIfsc(
  ifscCode: string,
  options?: { enabled?: boolean }
) {
  return useQuery(bankServiceCapabilitiesByIfscQueryOptions(ifscCode, options));
}

export const useMandates = (investmentAccountGuid: string, enabled = true) => {
  const mandatesResponse = useInfiniteQuery({
    queryKey: ["payments/mandates/list", investmentAccountGuid],
    queryFn: () => getMandatesApi(investmentAccountGuid),
    getNextPageParam: (lastPage) => {
      return lastPage.next ? lastPage.next.split("page=")[1] : undefined;
    },
    initialPageParam: "1",
    select: (queryResponse) => queryResponse.pages,
    enabled: !!investmentAccountGuid && enabled,
  });

  const {
    isFetching: isFetchingBankAccounts,
    data: bankAccounts,
    error: bankAccountsError,
  } = useInvestorBankAccounts(investmentAccountGuid);

  const mandates = useMemo(
    () =>
      mandatesResponse.data
        ?.flatMap((page) => page.results)
        .map((mandate) => ({
          ...mandate,
          bank_account_details: bankAccounts?.find(
            (bankAccount) => bankAccount.id === mandate.bank_account_id
          ),
        })),
    [mandatesResponse.data, bankAccounts]
  );

  return {
    ...mandatesResponse,
    isFetching: isFetchingBankAccounts || mandatesResponse.isFetching,
    error: mandatesResponse.error || bankAccountsError,
    mandates,
  };
};

export const useFetchMandatesMutation = () => {
  return useMutation({
    mutationFn: (variables: { investment_account_guid: string }) =>
      getMandatesApi(variables.investment_account_guid),
  });
};

export const useCreateMandate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMandateApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["payments/mandates/list"],
      });
    },
  });
};

export const useDeleteMandate = (investmentAccountGuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteMandateApi(id, investmentAccountGuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["payments/mandates/list"],
      });
    },
  });
};

export const useAuthorizeMandate = () => {
  return useMutation({
    mutationFn: ({
      id,
      investment_account_guid: investmentAccountGuid,
      sendEmail,
    }: {
      id: number;
      investment_account_guid: string;
      sendEmail: boolean;
    }) => authorizeMandateApi(id, investmentAccountGuid, sendEmail),
  });
};

export const useAuthMandate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      investment_account_guid: investmentAccountGuid,
    }: {
      id: number;
      investment_account_guid: string;
    }) => authMandateApi(id, investmentAccountGuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["payments/mandates/list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["mandates"],
      });
    },
  });
};

export const usePollMandateStatus = () => {
  return useMutation({
    mutationFn: ({
      id,
      investment_account_guid: investmentAccountGuid,
    }: {
      id: number;
      investment_account_guid: string;
    }) => pollMandateStatusApi(id, investmentAccountGuid),
  });
};

export const useMandateStatusReport = (investmentAccountGuid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => generateMandateStatusReportApi(investmentAccountGuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["payments/mandates/list"],
      });
    },
  });
};

export function useMandate(variables: {
  investmentAccountGuid: string;
  id: string | number;
}) {
  return useQuery({
    queryKey: ["mandates", variables.investmentAccountGuid, variables.id],
    queryFn: () =>
      fetchMandateByIdApi(variables.investmentAccountGuid, variables.id),
    enabled: !!variables.investmentAccountGuid && !!variables.id,
  });
}

export function useUploadMandate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadMandateNachFormApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["payments/mandates/list"],
      });
    },
  });
}

export function useGeneratePrefilledMandate() {
  return useMutation({
    mutationFn: generatePrefilledNACHMandate,
  });
}
